import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useData } from '../context/DataContext';
import { ChevronRight, Search as SearchIcon, SlidersHorizontal, X } from 'lucide-react';

const FALLBACK_CITIES = ['Hyderabad', 'Warangal', 'Nizamabad'];

const PRICE_MAX_CAP = 100000;

const SearchResultsPage = () => {
  const [params] = useSearchParams();
  const { cities: ctxCities = [] } = useData() || {};
  const TELANGANA_CITIES = ctxCities.length ? ctxCities.map((c) => c.name) : FALLBACK_CITIES;
  const q = params.get('q') || '';
  const cityParam = params.get('city') || 'All Telangana';

  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX_CAP]);
  const [selectedCities, setSelectedCities] = useState(() => (cityParam && cityParam !== 'All Telangana' ? [cityParam] : []));
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Debounce filter changes for nicer UX
  const filterKey = useMemo(() => JSON.stringify({ q, sortBy, priceRange, selectedCities, verifiedOnly }), [q, sortBy, priceRange, selectedCities, verifiedOnly]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      const payload = {
        q,
        sort: sortBy,
        limit: 60,
        price_min: priceRange[0],
        price_max: priceRange[1] < PRICE_MAX_CAP ? priceRange[1] : undefined,
      };
      if (selectedCities.length) payload.city = selectedCities.join(',');
      if (verifiedOnly) payload.verified_only = true;
      api.getProducts(payload)
        .then((d) => { setResults(d.items || []); setTotal(d.total || 0); })
        .catch(() => { setResults([]); setTotal(0); })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const toggleCity = (c) => {
    setSelectedCities((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const resetFilters = () => {
    setPriceRange([0, PRICE_MAX_CAP]);
    setSelectedCities([]);
    setVerifiedOnly(false);
    setSortBy('relevance');
  };

  const activeFilters =
    (priceRange[0] > 0 || priceRange[1] < PRICE_MAX_CAP ? 1 : 0) +
    selectedCities.length +
    (verifiedOnly ? 1 : 0);

  const inrFmt = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

  const Sidebar = (
    <aside className="bg-white border border-gray-200 rounded-md p-4 h-fit" data-testid="search-filters-sidebar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Refine Search</h3>
        {activeFilters > 0 && (
          <button onClick={resetFilters} data-testid="reset-filters-btn" className="text-xs text-[#ee5e23] hover:underline">Reset</button>
        )}
      </div>

      <div className="space-y-5">
        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Price Range</h4>
          <Slider
            min={0}
            max={PRICE_MAX_CAP}
            step={500}
            value={priceRange}
            onValueChange={setPriceRange}
            data-testid="price-range-slider"
            className="mb-3"
          />
          <div className="flex items-center justify-between text-xs text-gray-700">
            <span data-testid="price-min-label">{inrFmt(priceRange[0])}</span>
            <span data-testid="price-max-label">
              {priceRange[1] >= PRICE_MAX_CAP ? `${inrFmt(PRICE_MAX_CAP)}+` : inrFmt(priceRange[1])}
            </span>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Location</h4>
          <div className="max-h-44 overflow-y-auto pr-1">
            {TELANGANA_CITIES.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-[#2c54b2] py-1">
                <Checkbox
                  checked={selectedCities.includes(c)}
                  onCheckedChange={() => toggleCity(c)}
                  data-testid={`city-filter-${c.toLowerCase()}`}
                />
                {c}
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Seller Type</h4>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-[#2c54b2] py-1">
            <Checkbox
              checked={verifiedOnly}
              onCheckedChange={(v) => setVerifiedOnly(!!v)}
              data-testid="verified-only-filter"
            />
            Verified Vendors only
          </label>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-1 text-xs text-gray-600">
          <Link to="/" className="hover:text-[#2c54b2]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-900 font-medium">Search Results</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <SearchIcon className="w-5 h-5 text-[#ee5e23]" />
            <h1 className="text-xl font-bold text-gray-900" data-testid="search-results-heading">
              {loading ? 'Searching' : `${total} results`} {q && <>for &ldquo;<span className="text-[#ee5e23]">{q}</span>&rdquo;</>} in <span className="text-[#2c54b2]">{cityParam}</span>
            </h1>
          </div>
          <Button
            variant="outline"
            className="lg:hidden border-[#2c54b2] text-[#2c54b2]"
            onClick={() => setFiltersOpen(true)}
            data-testid="open-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4 mr-1" /> Filters {activeFilters > 0 && `(${activeFilters})`}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          <div className="hidden lg:block">{Sidebar}</div>

          <div>
            <div className="bg-white border border-gray-200 rounded-md px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-gray-700" data-testid="results-count">
                Showing {results.length} of {total} products
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  data-testid="sort-select"
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-[#ee5e23]"
                >
                  <option value="relevance">Relevance</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>

            {!loading && results.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-md py-16 text-center" data-testid="no-results">
                <p className="text-gray-700 font-medium">No products matched your filters.</p>
                <p className="text-sm text-gray-500 mt-1">Try widening the price range, removing cities, or a different keyword.</p>
                <Button onClick={resetFilters} className="mt-3 bg-[#ee5e23] hover:bg-[#d24e15] text-white">Reset filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="search-results-grid">
                {results.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setFiltersOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Filters</h3>
              <button onClick={() => setFiltersOpen(false)} data-testid="close-filters-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4">{Sidebar}</div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SearchResultsPage;
